import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import IntlTelInput from "react-intl-tel-input";
import {
  Autocomplete,
  GoogleMap,
  Marker,
  StandaloneSearchBox,
} from "@react-google-maps/api";
import { fetchEmbassyCities } from "../../api/embassyGoodConductAppointment";
import { fetchReverseByProvider, fetchReverseByProviderDeduped } from "../../api/masterLocation";

export const GOOGLE_MAPS_LIBRARIES = ["places"];
const MAPS_CONTAINER_STYLE = { width: "100%", height: "320px" };
const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };

export const EMPTY_ADDRESS = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  mobileDialCode: "",
  mobileISOCode: "ae",
  isMobileValid: false,
  landline: "",
  companyName: "",
  country: "",
  countryCode: "",
  state: "",
  cityCode: "",
  cityName: "",
  cityId: "",
  area: "",
  street: "",
  building: "",
  apartmentNo: "",
  floor: "",
  zip: "",
  additionalAddress: "",
  coords: null,
};

export const EMPTY_ERRORS = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  cityCode: "",
  area: "",
  street: "",
  building: "",
  apartmentNo: "",
  coords: "",
};

export function validateEmail(v) {
  return /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}/.test(v);
}

export function validateAddress(addr, options = {}) {
  const { requireCoords = true } = options;
  const errs = { ...EMPTY_ERRORS };
  if (!addr.firstName.trim()) errs.firstName = "ERROR_SHIPPING_FIRST_NAME";
  if (!addr.lastName.trim()) errs.lastName = "ERROR_SHIPPING_LAST_NAME";
  if (!addr.email.trim() || !validateEmail(addr.email)) errs.email = "ERROR_SHIPPING_EMAIL";
  if (!addr.isMobileValid) errs.mobile = "ERROR_SHIPPING_MOBILE";
  if (!addr.cityCode) errs.cityCode = "ERROR_SHIPPING_CITY";
  if (!addr.area || !String(addr.area).trim()) errs.area = "ERROR_SHIPPING_AREA";
  if (!addr.street.trim()) errs.street = "ERROR_SHIPPING_STREET";
  if (!addr.building.trim()) errs.building = "ERROR_SHIPPING_BUILDING";
  if (!addr.apartmentNo.trim()) errs.apartmentNo = "ERROR_SHIPPING_APARTMENT";
  if (requireCoords && !addr.coords) errs.coords = "SHIPPING_LOCATION_REQUIRED";
  return errs;
}

export function hasErrors(errs) {
  return Object.values(errs).some(Boolean);
}

/**
 * Resolve Masters region `code` when `areaHint` is already a list code.
 * Used for sender `senderAddress.area` only.
 */
export function regionCodeFromMastersList(list, areaHint) {
  if (!list?.length || areaHint == null) return null;
  const raw = String(areaHint).trim();
  if (!raw) return null;
  const found = list.find((a) => String(a.code) === raw);
  return found != null ? found.code : null;
}

/** Numeric emirate / city master id from ReverseByProvider when present. */
function emirateMasterIdFromReverse(reverse) {
  const raw =
    reverse.emirateId ??
    reverse.EmirateId ??
    reverse.emirateMasterId ??
    reverse.EmirateMasterId ??
    reverse.cityId ??
    reverse.CityId ??
    null;
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Resolve Masters emirate `id` (for Regions API) from reverse `cityCode` only. */
function resolveEmirateIdFromMasters(list, cityCode) {
  if (!list || !list.length) return null;
  const code = cityCode != null ? String(cityCode).trim() : "";
  if (!code) return null;
  const byCode = list.find(
    (c) => String(c.code).toLowerCase() === code.toLowerCase()
  );
  return byCode != null && byCode.id != null ? Number(byCode.id) : null;
}

/** Map MasterLocation ReverseByProvider body to form fields (AE vs non-AE). */
function fieldsFromReverseResponse(reverse, prevForm) {
  const cc = String(
    reverse.countryCode ?? reverse.CountryCode ?? ""
  )
    .trim()
    .toUpperCase();
  const countryName = String(reverse.country ?? reverse.countryName ?? "").trim();
  const emirate = reverse.emirate != null ? String(reverse.emirate).trim() : "";
  const emirateCode =
    reverse.emirateCode != null ? String(reverse.emirateCode).trim() : "";
  const region = reverse.region != null ? String(reverse.region).trim() : "";
  const district = reverse.district != null ? String(reverse.district).trim() : "";
  const areaFromApi = reverse.area != null ? String(reverse.area).trim() : "";
  const stateVal = reverse.state != null ? String(reverse.state).trim() : "";

  let cityName;
  let cityCodeVal;
  let areaStr;

  if (cc === "AE") {
    cityName = emirate;
    cityCodeVal = emirateCode || emirate || cityName;
    areaStr = region || district || areaFromApi;
  } else {
    cityName = stateVal || emirate;
    cityCodeVal = emirateCode || stateVal || cityName || cc;
    areaStr = areaFromApi || district || region;
  }

  if (!cityCodeVal) cityCodeVal = cityName || cc;
  if (!areaStr) areaStr = district || areaFromApi || region;

  const street = reverse.street != null ? String(reverse.street).trim() : "";
  const building =
    reverse.building != null ? String(reverse.building).trim() : "";
  const zip =
    reverse.postalCode != null
      ? String(reverse.postalCode).trim()
      : reverse.zipCode != null
        ? String(reverse.zipCode).trim()
        : "";

  const emirateId = emirateMasterIdFromReverse(reverse);

  return {
    country: countryName || prevForm.country,
    countryCode: cc,
    state: stateVal,
    cityName,
    cityCode: cityCodeVal,
    cityId: emirateId != null ? emirateId : "",
    area: areaStr,
    street: street || prevForm.street,
    building: building || prevForm.building,
    zip: zip || prevForm.zip,
  };
}

const EMBASSY_CITY_DEBOUNCE_MS = 350;

function formatEmbassyCityRow(item) {
  const name = item.cityName != null ? String(item.cityName).trim() : "";
  const pc =
    item.postalCode != null ? String(item.postalCode).trim() : "";
  return pc ? `${name} — ${pc}` : name;
}

function embassyCityStableCode(item) {
  const name = item.cityName != null ? String(item.cityName).trim() : "";
  const pc =
    item.postalCode != null ? String(item.postalCode).trim() : "";
  return `${name}|${pc}`;
}

function EmbassyProvinceCityCombo({
  formData,
  onChange,
  accessToken,
  countryCode,
  sectionKey,
  t,
  locale,
  invalid,
}) {
  const inputId = `${sectionKey}-embassy-province`;
  const listboxId = `${sectionKey}-embassy-city-listbox`;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const committedLabelRef = useRef(null);
  const abortRef = useRef(null);
  const blurCloseTimerRef = useRef(null);

  const patchClearCity = useCallback(() => {
    ["cityCode", "cityName", "state", "zip", "cityId"].forEach((name) => {
      onChange({ target: { name, value: "" } });
    });
  }, [onChange]);

  useEffect(() => {
    if (!formData.cityCode || !String(formData.cityCode).trim()) {
      committedLabelRef.current = null;
      return;
    }
    const name = formData.cityName || "";
    const z = formData.zip || "";
    const label = z ? `${name} — ${z}` : name;
    committedLabelRef.current = label;
    setQuery(label);
  }, [formData.cityCode, formData.cityName, formData.zip]);

  useEffect(() => {
    if (!accessToken) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    const q = query.trim();
    if (q.length < 3) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    if (
      committedLabelRef.current !== null &&
      committedLabelRef.current === query
    ) {
      setResults([]);
      return undefined;
    }
    const tid = window.setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setLoading(true);
      fetchEmbassyCities({
        countryCode,
        cityPrefix: q,
        accessToken,
        signal: ac.signal,
      })
        .then((list) => {
          if (!ac.signal.aborted) {
            const arr = Array.isArray(list) ? list : [];
            setResults(arr);
            setHighlighted(arr.length ? 0 : -1);
          }
        })
        .catch((err) => {
          if (err && err.name === "AbortError") return;
          if (!ac.signal.aborted) {
            setResults([]);
            setHighlighted(-1);
          }
        })
        .finally(() => {
          if (!ac.signal.aborted) setLoading(false);
        });
    }, EMBASSY_CITY_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(tid);
    };
  }, [query, accessToken, countryCode]);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
      if (blurCloseTimerRef.current) window.clearTimeout(blurCloseTimerRef.current);
    },
    []
  );

  const chooseItem = useCallback(
    (item) => {
      const cn =
        item.cityName != null ? String(item.cityName).trim() : "";
      const pc =
        item.postalCode != null ? String(item.postalCode).trim() : "";
      const label = formatEmbassyCityRow(item);
      const codeVal = embassyCityStableCode(item);
      committedLabelRef.current = label;
      setQuery(label);
      setOpen(false);
      setHighlighted(-1);
      setResults([]);
      onChange({ target: { name: "cityName", value: cn } });
      onChange({ target: { name: "cityCode", value: codeVal } });
      onChange({ target: { name: "state", value: cn } });
      onChange({ target: { name: "zip", value: pc } });
      onChange({ target: { name: "cityId", value: "" } });
    },
    [onChange]
  );

  const handleInputChange = (e) => {
    const v = e.target.value;
    const hadCommit =
      committedLabelRef.current !== null && Boolean(formData.cityCode);
    const breakingCommit =
      hadCommit && v !== committedLabelRef.current;

    if (breakingCommit) {
      committedLabelRef.current = null;
      patchClearCity();
    }
    setQuery(v);
    if (accessToken && v.trim().length >= 3) {
      setOpen(true);
    } else {
      setOpen(false);
      setResults([]);
      setHighlighted(-1);
    }
  };

  const handleInputBlur = () => {
    blurCloseTimerRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  const handleInputFocus = () => {
    if (blurCloseTimerRef.current) {
      window.clearTimeout(blurCloseTimerRef.current);
      blurCloseTimerRef.current = null;
    }
    if (
      accessToken &&
      query.trim().length >= 3 &&
      committedLabelRef.current !== query
    ) {
      setOpen(true);
    }
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Escape") setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => (h + 1 >= results.length ? 0 : h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => (h <= 0 ? results.length - 1 : h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = highlighted >= 0 ? results[highlighted] : results[0];
      if (item) chooseItem(item);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const sessionMissing = !accessToken;
  const showListActually =
    open &&
    !sessionMissing &&
    query.trim().length >= 3 &&
    !(committedLabelRef.current !== null && query === committedLabelRef.current);

  return (
    <div className="position-relative">
      <input
        id={inputId}
        type="text"
        className={`form-control${invalid ? " is-invalid" : ""}`}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showListActually && results.length > 0}
        aria-controls={showListActually && results.length > 0 ? listboxId : undefined}
        value={query}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={
          sessionMissing
            ? locale === "ar"
              ? "يجب تسجيل الدخول"
              : "Session required"
            : locale === "ar"
              ? "اكتب 3 أحرف على الأقل"
              : "Type at least 3 letters"
        }
        disabled={sessionMissing}
      />
      {showListActually && (
        <ul
          id={listboxId}
          role="listbox"
          className="list-group gc-embassy-city-list position-absolute w-100 mt-1 shadow-sm"
          style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}
        >
          {loading ? (
            <li className="list-group-item text-muted small" role="status">
              <span className="spinner-border spinner-border-sm me-2 align-middle" aria-hidden />
              {t("LOADING_PLEASE_WAIT")}
            </li>
          ) : results.length === 0 ? (
            <li className="list-group-item text-muted small" role="status">
              {locale === "ar" ? "لا توجد نتائج" : "No results"}
            </li>
          ) : (
            results.map((item, idx) => {
              const label = formatEmbassyCityRow(item);
              return (
                <li
                  key={`${embassyCityStableCode(item)}-${idx}`}
                  role="option"
                  aria-selected={idx === highlighted}
                  className={`list-group-item list-group-item-action gc-embassy-city-option small${
                    idx === highlighted ? " active" : ""
                  }`}
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    chooseItem(item);
                  }}
                  onMouseEnter={() => setHighlighted(idx)}
                >
                  {label}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

function ShippingAddressSection({
  formData,
  errors,
  cityLookup,
  areaLookup,
  onChange,
  onPhoneChange,
  onFlagChange,
  onCityChange,
  hasCompanyName,
  t,
  locale,
  sectionKey,
  restrictToCountry,
  reverseByProviderLocation,
  onReverseGeocodePendingChange,
  /** Receiver: keep region editable when reverse provider is enabled. */
  lockAreaWhenReverse = true,
  /** Force area field readonly/disabled regardless of provider mode (sender requirement). */
  forceAreaReadonly = false,
  /** Sender only: keep street editable on receiver; lock street when using Reverse on sender. */
  lockStreetWhenReverse = false,
  /** When false, map/search UI is hidden (UAE dropdown flow). */
  showMap = true,
  /** Override the initial map center when no coords are pinned yet. */
  defaultCenter = DEFAULT_CENTER,
  /** Egypt receiver: searchable State/Province via embassy Cities API. */
  embassyCitySearch = null,
  /** When set, country field always shows this (ignores stale formData from edit/back). */
  fixedCountry = null,
}) {
  const searchBoxRef = useRef(null);
  const mapRef = useRef(null);
  const [outOfCountryError, setOutOfCountryError] = useState(false);
  const [reverseGeocodeError, setReverseGeocodeError] = useState(false);
  const [mapSearchText, setMapSearchText] = useState("");
  const [isReverseGeocodePending, setIsReverseGeocodePending] = useState(false);
  const setReversePending = useCallback(
    (isPending) => {
      setIsReverseGeocodePending(isPending);
      if (typeof onReverseGeocodePendingChange === "function") {
        onReverseGeocodePendingChange(isPending);
      }
    },
    [onReverseGeocodePendingChange]
  );

  const reverseLatLng = useCallback(
    async (lat, lng, countryCodeHint = null) => {
      if (!reverseByProviderLocation) return;
      const { accessToken, locale: loc } = reverseByProviderLocation;
      setReverseGeocodeError(false);
      setReversePending(true);

      try {
        const hintIsAE =
          countryCodeHint && String(countryCodeHint).toUpperCase() === "AE";
        const initialProvider = hintIsAE ? 1 : 0;

        let reverse = await fetchReverseByProviderDeduped(lat, lng, {
          accessToken,
          locale: loc,
          provider: initialProvider,
        });

        // No country hint was given but the result came back as AE — re-fetch
        // with provider 1 to get accurate emirate/region data.
        // Use the non-deduped call because dedup keying ignores provider.
        if (
          reverse &&
          !hintIsAE &&
          String(reverse.countryCode ?? "").toUpperCase() === "AE"
        ) {
          const reverseAE = await fetchReverseByProvider(lat, lng, {
            accessToken,
            locale: loc,
            provider: 1,
          });
          if (reverseAE) reverse = reverseAE;
        }

        if (!reverse) {
          setReverseGeocodeError(true);
          return;
        }

        const mapped = fieldsFromReverseResponse(reverse, formData);

        if (
          restrictToCountry &&
          mapped.countryCode &&
          mapped.countryCode.toLowerCase() !== String(restrictToCountry).toLowerCase()
        ) {
          setOutOfCountryError(true);
          return;
        }

        setOutOfCountryError(false);
        const label = [mapped.street, mapped.area, mapped.cityName, mapped.country]
          .filter(Boolean)
          .join(", ");
        if (label) setMapSearchText(label);

        onChange({
          _reverseProvider: true,
          coords: { lat, lng },
          ...mapped,
        });
      } finally {
        setReversePending(false);
      }
    },
    [formData, onChange, restrictToCountry, reverseByProviderLocation, setReversePending]
  );

  // Hydrate search box when the step loads with saved coordinates (label from form; reverse API fills fields).
  useEffect(() => {
    if (!showMap) return;
    if (!formData.coords) return;
    const label = [formData.street, formData.area, formData.cityName, formData.country]
      .filter(Boolean)
      .join(", ");
    if (label) setMapSearchText(label);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount when coords exist
  }, [showMap]);

  // Keep map viewport aligned with the marker when coords change (search, drag, or pin drop).
  useEffect(() => {
    if (!showMap) return;
    const map = mapRef.current;
    if (!map || !formData.coords) return;
    map.panTo(formData.coords);
    map.setZoom(16);
  }, [showMap, formData.coords?.lat, formData.coords?.lng]);

  const handlePlacesChanged = () => {
    const places = searchBoxRef.current?.getPlaces();
    if (!places || places.length === 0) return;
    const place = places[0];
    if (!place?.geometry?.location) return;
    if (place.formatted_address) {
      setMapSearchText(place.formatted_address);
    }
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    if (reverseByProviderLocation) {
      const countryCode =
        (place.address_components || []).find((c) =>
          c.types.includes("country")
        )?.short_name || null;
      void reverseLatLng(lat, lng, countryCode);
    }
  };

  const handleAutocompletePlaceChanged = () => {
    const place = searchBoxRef.current?.getPlace();
    if (!place?.geometry?.location) return;

    const components = place.address_components || [];
    const countryCode =
      components.find((p) => p.types.includes("country"))?.short_name || "";

    if (
      restrictToCountry &&
      countryCode.toLowerCase() !== String(restrictToCountry).toLowerCase()
    ) {
      setOutOfCountryError(true);
      return;
    }

    if (place.formatted_address) {
      setMapSearchText(place.formatted_address);
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    if (reverseByProviderLocation) {
      void reverseLatLng(lat, lng, countryCode || null);
    }
  };

  const handleMapClick = (e) => {
    if (reverseByProviderLocation) {
      void reverseLatLng(e.latLng.lat(), e.latLng.lng(), formData.countryCode || null);
    }
  };

  const handleMarkerDrag = (e) => {
    if (reverseByProviderLocation) {
      void reverseLatLng(e.latLng.lat(), e.latLng.lng(), formData.countryCode || null);
    }
  };

  const isReverseProvider = Boolean(reverseByProviderLocation);
  const embassyProvinceMode = Boolean(
    embassyCitySearch && !isReverseProvider
  );
  const displayCountryName = fixedCountry?.name ?? formData.country;
  const displayCountryCode = fixedCountry?.code ?? formData.countryCode;
  const countryMatchesRestrict =
    Boolean(fixedCountry) ||
    (Boolean(restrictToCountry) &&
      displayCountryCode &&
      String(displayCountryCode).toLowerCase() ===
        String(restrictToCountry).toLowerCase());
  const phoneDefaultCountry =
    restrictToCountry?.toLowerCase() === "eg"
      ? "eg"
      : formData.mobileISOCode || "ae";
  const readonlyClass = "form-control bg-light";

  const reverseAreaDisplay = (() => {
    if (!isReverseProvider || !formData.area) return formData.area || "";
    const found = areaLookup.find((a) => String(a.code) === String(formData.area));
    if (found) return locale === "ar" ? found.nameAr : found.nameEn;
    return formData.area;
  })();
  const markerPosition = formData.coords || defaultCenter;

  return (
    <div>
      {/* ── Contact details ── */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.firstName ? " invalid" : ""}`}>
            <label htmlFor={`${sectionKey}-firstName`}>
              {t("FIRST_NAME")} <span>*</span>
            </label>
            <input
              id={`${sectionKey}-firstName`}
              name="firstName"
              type="text"
              className="form-control"
              value={formData.firstName}
              onChange={onChange}
            />
            {errors.firstName && (
              <div className="error-message">{t("ERROR_SHIPPING_FIRST_NAME")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.lastName ? " invalid" : ""}`}>
            <label htmlFor={`${sectionKey}-lastName`}>
              {t("LAST_NAME")} <span>*</span>
            </label>
            <input
              id={`${sectionKey}-lastName`}
              name="lastName"
              type="text"
              className="form-control"
              value={formData.lastName}
              onChange={onChange}
            />
            {errors.lastName && (
              <div className="error-message">{t("ERROR_SHIPPING_LAST_NAME")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.email ? " invalid" : ""}`}>
            <label htmlFor={`${sectionKey}-email`}>
              {t("EMAIL_ADDRESS")} <span>*</span>
            </label>
            <input
              id={`${sectionKey}-email`}
              name="email"
              type="email"
              className="form-control"
              value={formData.email}
              onChange={onChange}
              maxLength={100}
            />
            {errors.email && (
              <div className="error-message">{t("ERROR_SHIPPING_EMAIL")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.mobile ? " invalid" : ""}`}>
            <label>{t("MOBILE_NUMBER")} <span>*</span></label>
            <IntlTelInput
              preferredCountries={["ae", "eg"]}
              containerClassName="intl-tel-input"
              inputClassName="form-control"
              onPhoneNumberChange={onPhoneChange}
              onSelectFlag={onFlagChange}
              value={formData.mobile}
              separateDialCode
              defaultCountry={phoneDefaultCountry}
            />
            {errors.mobile && (
              <div className="error-message">{t("ERROR_SHIPPING_MOBILE")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor={`${sectionKey}-landline`}>{t("SHIPPING_LANDLINE")}</label>
            <input
              id={`${sectionKey}-landline`}
              name="landline"
              type="text"
              className="form-control"
              value={formData.landline}
              onChange={onChange}
            />
          </div>
        </div>

        {hasCompanyName && (
          <div className="col-md-6 mb-3">
            <div className="form-group">
              <label htmlFor={`${sectionKey}-companyName`}>
                {t("SHIPPING_COMPANY_NAME")}
              </label>
              <input
                id={`${sectionKey}-companyName`}
                name="companyName"
                type="text"
                className="form-control"
                value={formData.companyName}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>

      {showMap ? (
        <>
          <hr className="my-3" />

          {/* ── Map ── */}
          <div className="gc-section-heading gc-section-heading--navy mt-3">
            <span>{t("SHIPPING_SEARCH_LOCATION")}</span>
          </div>
          <div
            className="mb-3 p-3 rounded"
            style={{ background: "#e8f0fe", borderLeft: "4px solid #01259c" }}
          >
            <small className="fw-semibold" style={{ color: "#01259c" }}>
              {t("SHIPPING_SEARCH_LOCATION_INFO")}
            </small>
          </div>

          <div
            className={`form-group mb-3${
              errors.coords || outOfCountryError ? " invalid" : ""
            }`}
          >
            {restrictToCountry ? (
              <Autocomplete
                onLoad={(ref) => { searchBoxRef.current = ref; }}
                onPlaceChanged={handleAutocompletePlaceChanged}
                options={{
                  componentRestrictions: { country: restrictToCountry },
                  fields: ["geometry", "address_components", "formatted_address"],
                }}
              >
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={t("SHIPPING_SEARCH_LOCATION")}
                  value={mapSearchText}
                  onChange={(e) => setMapSearchText(e.target.value)}
                />
              </Autocomplete>
            ) : (
              <StandaloneSearchBox
                onLoad={(ref) => { searchBoxRef.current = ref; }}
                onPlacesChanged={handlePlacesChanged}
              >
                <input
                  type="text"
                  className="form-control mb-2"
                  placeholder={t("SHIPPING_SEARCH_LOCATION")}
                  value={mapSearchText}
                  onChange={(e) => setMapSearchText(e.target.value)}
                />
              </StandaloneSearchBox>
            )}
            <div className="position-relative">
              <GoogleMap
                mapContainerStyle={MAPS_CONTAINER_STYLE}
                center={formData.coords || defaultCenter}
                zoom={formData.coords ? 16 : 11}
                onLoad={(map) => {
                  mapRef.current = map;
                  if (formData.coords) {
                    map.panTo(formData.coords);
                    map.setZoom(16);
                  }
                }}
                onClick={handleMapClick}
              >
                <Marker
                  position={markerPosition}
                  draggable
                  onDragEnd={handleMarkerDrag}
                />
              </GoogleMap>
              {isReverseGeocodePending && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "rgba(255, 255, 255, 0.7)", zIndex: 2 }}
                  role="status"
                  aria-live="polite"
                >
                  <div className="d-flex align-items-center px-3 py-2 rounded bg-white shadow-sm">
                    <div className="spinner-border spinner-border-sm text-primary me-2" aria-hidden="true" />
                    <span>{t("LOADING_PLEASE_WAIT")}</span>
                  </div>
                </div>
              )}
            </div>
            {errors.coords && (
              <div className="error-message mt-1">{t("SHIPPING_LOCATION_REQUIRED")}</div>
            )}
            {reverseGeocodeError && (
              <div className="error-message mt-1">{t("SHIPPING_REVERSE_GEOCODE_FAILED")}</div>
            )}
            {outOfCountryError && (
              <div className="error-message mt-1">
                {restrictToCountry?.toLowerCase() === "eg"
                  ? t("SHIPPING_LOCATION_EG_ONLY")
                  : t("SHIPPING_LOCATION_UAE_ONLY")}
              </div>
            )}
          </div>
        </>
      ) : (
        <hr className="my-3" />
      )}

      {/* ── Address fields ── */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label>{t("SHIPPING_COUNTRY")}</label>
            <input
              type="text"
              className={readonlyClass}
              value={displayCountryName}
              readOnly
              disabled={countryMatchesRestrict || !isReverseProvider}
            />
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.cityCode ? " invalid" : ""}`}>
            <label
              htmlFor={
                embassyProvinceMode
                  ? `${sectionKey}-embassy-province`
                  : `${sectionKey}-city`
              }
            >
              {(isReverseProvider || embassyProvinceMode)
                ? t("SHIPPING_STATE_PROVINCE")
                : t("SHIPPING_CITY")}{" "}
              <span>*</span>
            </label>
            {embassyProvinceMode ? (
              <EmbassyProvinceCityCombo
                formData={formData}
                onChange={onChange}
                accessToken={
                  embassyCitySearch && embassyCitySearch.accessToken
                    ? embassyCitySearch.accessToken
                    : ""
                }
                countryCode={
                  (embassyCitySearch && embassyCitySearch.countryCode) || "EG"
                }
                sectionKey={sectionKey}
                locale={locale}
                t={t}
                invalid={Boolean(errors.cityCode)}
              />
            ) : isReverseProvider ? (
              <input
                id={`${sectionKey}-city`}
                type="text"
                className={readonlyClass}
                value={formData.cityName || ""}
                readOnly
              />
            ) : (
              <select
                id={`${sectionKey}-city`}
                name="cityCode"
                className="form-control"
                value={formData.cityCode}
                onChange={onCityChange}
              >
                <option value="">
                  {locale === "ar" ? "اختر الإمارة" : "Select Emirate"}
                </option>
                {cityLookup.map((city) => (
                  <option key={city.code} value={city.code}>
                    {locale === "ar" ? city.nameAr : city.nameEn}
                  </option>
                ))}
              </select>
            )}
            {errors.cityCode && (
              <div className="error-message">{t("ERROR_SHIPPING_CITY")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.area ? " invalid" : ""}`}>
            <label htmlFor={`${sectionKey}-area`}>
              {t("SHIPPING_AREA")} <span>*</span>
            </label>
            {isReverseProvider ? (
              <input
                id={`${sectionKey}-area`}
                type="text"
                className={lockAreaWhenReverse || forceAreaReadonly ? readonlyClass : "form-control"}
                value={reverseAreaDisplay}
                readOnly={lockAreaWhenReverse || forceAreaReadonly}
                {...(lockAreaWhenReverse || forceAreaReadonly ? {} : { name: "area", onChange })}
              />
            ) : areaLookup.length > 0 ? (
              <select
                id={`${sectionKey}-area`}
                name="area"
                className="form-control"
                value={formData.area}
                onChange={onChange}
                disabled={forceAreaReadonly}
              >
                <option value="">
                  {locale === "ar" ? "اختر المنطقة" : "Select Area"}
                </option>
                {areaLookup.map((area) => (
                  <option key={area.code} value={area.code}>
                    {locale === "ar" ? area.nameAr : area.nameEn}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`${sectionKey}-area`}
                name="area"
                type="text"
                className="form-control"
                value={formData.area}
                onChange={onChange}
                readOnly={forceAreaReadonly}
              />
            )}
            {errors.area && (
              <div className="error-message">{t("ERROR_SHIPPING_AREA")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.street ? " invalid" : ""}`}>
            <label htmlFor={`${sectionKey}-street`}>
              {t("SHIPPING_STREET")} <span>*</span>
            </label>
            <input
              id={`${sectionKey}-street`}
              name="street"
              type="text"
              className={
                isReverseProvider && lockStreetWhenReverse ? readonlyClass : "form-control"
              }
              value={formData.street}
              {...(isReverseProvider && lockStreetWhenReverse ? {} : { onChange })}
              readOnly={isReverseProvider && lockStreetWhenReverse}
              maxLength={100}
            />
            {errors.street && (
              <div className="error-message">{t("ERROR_SHIPPING_STREET")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.building ? " invalid" : ""}`}>
            <label htmlFor={`${sectionKey}-building`}>
              {isReverseProvider ? t("SHIPPING_BUILDING_VILLA_NAME") : t("SHIPPING_BUILDING")}{" "}
              <span>*</span>
            </label>
            <input
              id={`${sectionKey}-building`}
              name="building"
              type="text"
              className="form-control"
              value={formData.building}
              onChange={onChange}
              maxLength={100}
            />
            {errors.building && (
              <div className="error-message">{t("ERROR_SHIPPING_BUILDING")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className={`form-group${errors.apartmentNo ? " invalid" : ""}`}>
            <label htmlFor={`${sectionKey}-apartmentNo`}>
              {isReverseProvider ? t("SHIPPING_APARTMENT_VILLA_NO") : t("SHIPPING_APARTMENT")}{" "}
              <span>*</span>
            </label>
            <input
              id={`${sectionKey}-apartmentNo`}
              name="apartmentNo"
              type="text"
              className="form-control"
              value={formData.apartmentNo}
              onChange={onChange}
              maxLength={20}
            />
            {errors.apartmentNo && (
              <div className="error-message">{t("ERROR_SHIPPING_APARTMENT")}</div>
            )}
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor={`${sectionKey}-floor`}>{t("SHIPPING_FLOOR")}</label>
            <input
              id={`${sectionKey}-floor`}
              name="floor"
              type="text"
              className="form-control"
              value={formData.floor}
              onChange={onChange}
              maxLength={10}
            />
          </div>
        </div>

        <div className="col-md-6 mb-3">
          <div className="form-group">
            <label htmlFor={`${sectionKey}-zip`}>{t("SHIPPING_ZIP")}</label>
            <input
              id={`${sectionKey}-zip`}
              name="zip"
              type="text"
              className="form-control"
              value={formData.zip}
              onChange={onChange}
              maxLength={20}
            />
          </div>
        </div>

        <div className="col-12 mb-3">
          <div className="form-group">
            <label htmlFor={`${sectionKey}-additionalAddress`}>
              {t("SHIPPING_ADDITIONAL_ADDRESS")}
            </label>
            <textarea
              id={`${sectionKey}-additionalAddress`}
              name="additionalAddress"
              className="form-control"
              rows={3}
              value={formData.additionalAddress}
              onChange={onChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function useShippingForm({
  initialData,
  /** When this changes (e.g. return from review edit), form state is reset from initialData. */
  hydrateKey,
  cityLookup,
  fetchAreas,
  mastersEmirateList = [],
  /** Sender only: map area label → Masters region code when Regions list is loaded. */
  syncAreaToMastersRegionCode = false,
  /** Options forwarded to validateAddress (e.g. { requireCoords: false } for sender). */
  validateOptions = {},
}) {
  const [formData, setFormData] = useState(
    initialData ? { ...EMPTY_ADDRESS, ...initialData } : { ...EMPTY_ADDRESS }
  );
  const [errors, setErrors] = useState({ ...EMPTY_ERRORS });
  const [areaLookup, setAreaLookup] = useState([]);
  const [touched, setTouched] = useState({});

  const initialDataKey = useMemo(
    () =>
      initialData
        ? `${initialData.countryCode}|${initialData.country}|${initialData.email}|${initialData.cityCode}`
        : "",
    [
      initialData?.countryCode,
      initialData?.country,
      initialData?.email,
      initialData?.cityCode,
    ]
  );

  useEffect(() => {
    if (hydrateKey === undefined) return;
    setFormData(initialData ? { ...EMPTY_ADDRESS, ...initialData } : { ...EMPTY_ADDRESS });
    setErrors({ ...EMPTY_ERRORS });
    setTouched({});
    if (initialData?.cityId) {
      fetchAreas(initialData.cityId, setAreaLookup);
    } else {
      setAreaLookup([]);
    }
  }, [hydrateKey, initialDataKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hydrateKey !== undefined) return;
    if (initialData?.cityId) {
      fetchAreas(initialData.cityId, setAreaLookup);
    }
  }, []);

  /** Sender only: after Regions load, persist Masters region `code` in form state. */
  useEffect(() => {
    if (!syncAreaToMastersRegionCode || !formData.area || areaLookup.length === 0) return;
    const mapped = regionCodeFromMastersList(areaLookup, formData.area);
    if (mapped != null && mapped !== formData.area) {
      setFormData((prev) => ({ ...prev, area: mapped }));
    }
  }, [syncAreaToMastersRegionCode, areaLookup, formData.area]);

  /** Re-validate only the fields the user has already touched. */
  useEffect(() => {
    if (!Object.values(touched).some(Boolean)) return;
    const allErrors = validateAddress(formData, validateOptions);
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(allErrors).forEach((k) => {
        if (touched[k]) next[k] = allErrors[k];
      });
      return next;
    });
  }, [formData, touched, validateOptions]);

  /** Mark every validatable field as touched and surface all errors immediately. */
  const touchAll = useCallback(() => {
    setTouched(
      Object.keys(EMPTY_ERRORS).reduce((acc, k) => ({ ...acc, [k]: true }), {})
    );
  }, []);

  const handleChange = useCallback((e) => {
    if (e._reverseProvider) {
      const { _reverseProvider, ...fields } = e;
      let emirateId =
        fields.cityId != null && fields.cityId !== ""
          ? Number(fields.cityId)
          : NaN;
      if (!Number.isFinite(emirateId) || emirateId <= 0) {
        emirateId = resolveEmirateIdFromMasters(
          mastersEmirateList,
          fields.cityCode
        );
      }
      const nextFields =
        emirateId != null && Number.isFinite(emirateId) && emirateId > 0
          ? { ...fields, cityId: emirateId }
          : { ...fields, cityId: fields.cityId || "" };
      setFormData((prev) => ({ ...prev, ...nextFields }));
      if (emirateId != null && Number.isFinite(emirateId) && emirateId > 0) {
        fetchAreas(emirateId, setAreaLookup);
      } else {
        setAreaLookup([]);
      }
      return;
    }
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, [fetchAreas, mastersEmirateList]);

  const handlePhoneChange = useCallback((isValid, value, countryData) => {
    setTouched((prev) => ({ ...prev, mobile: true }));
    setFormData((prev) => ({
      ...prev,
      mobile: value,
      mobileDialCode: countryData?.dialCode || prev.mobileDialCode,
      mobileISOCode: countryData?.iso2 || prev.mobileISOCode,
      isMobileValid: isValid,
    }));
  }, []);

  const handleFlagChange = useCallback((currentNumber, countryData) => {
    setFormData((prev) => ({
      ...prev,
      mobileDialCode: countryData?.dialCode || prev.mobileDialCode,
      mobileISOCode: countryData?.iso2 || prev.mobileISOCode,
    }));
  }, []);

  const handleCityChange = useCallback((e, cityLookupList) => {
    const code = e.target.value;
    const city = cityLookupList.find((c) => String(c.code) === String(code));
    setTouched((prev) => ({ ...prev, cityCode: true }));
    setAreaLookup([]);
    setFormData((prev) => ({
      ...prev,
      cityCode: code,
      cityName: city ? city.nameEn : "",
      cityId: city ? city.id : "",
      area: "",
    }));
    if (city) fetchAreas(city.id, setAreaLookup);
  }, [fetchAreas]);

  return {
    formData,
    errors,
    setErrors,
    areaLookup,
    handleChange,
    handlePhoneChange,
    handleFlagChange,
    handleCityChange,
    touchAll,
  };
}

export { ShippingAddressSection };
export default ShippingAddressSection;
